import {
  MinimalTarget,
  MinimalTargetAttributeDtoValue,
  TargetAttributeDto,
  TargetAttributeDtoType,
  TargetAttributeDtoValue,
  VehicleTypeDto,
} from '@/generated/model';
import {
  FieldType,
  IComboSlot,
  IDateSlot,
  ITextSlot,
  ITreeSlot,
  One,
  OneSlotFactory,
  TypedField,
} from 'react-declarative';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  getCommonTargetAttributes,
  getPossibleAttributeValues,
  getVehicleTypes,
  updateTargetAttributes,
} from '@/generated/client/targetattributes/targetattributes';
import IData from 'react-declarative/components/RecordView/model/IData';
import { useTranslation } from 'react-i18next';
import { getContent, translateKey } from './TargetAttributesCommon';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotificationStore } from '@/stores/notificationstore';
import TargetAttributesSaveButton from './TargetAttributesSaveButton';
import { SearchComponent } from '@/components/common/Search';
import { ArrowBack, FileDownload } from '@mui/icons-material';
import { ctxTargetAttributes } from '@/contexts/TargetAttributesContext';
import UnsavedDrawer from './UnsavedDrawer';
import { targetAttributesExport } from './TargetAttributesExport';
import { ctxUser } from '@/contexts/UserContext';
import DisplayNoResult from '../common/DisplayNoResult';
import { isRunningInApp } from '@/utilities/sizeUtils';
import { DateTimePickerSelect } from '@/components/common/date/DateTimePickerSelect';
import MobileVehicleTypeSelector from './MobileVehicleTypeSelector';
import { Popup } from '../common/Popup';

const integerAttributeTypes = new Set<TargetAttributeDtoType>([
  TargetAttributeDtoType['@int'],
  TargetAttributeDtoType['@long'],
]);
const numericAttributeTypes = new Set<TargetAttributeDtoType>([
  TargetAttributeDtoType['@int'],
  TargetAttributeDtoType['@long'],
  TargetAttributeDtoType['@double'],
]);

export default function TargetAttributesMobileForm({
  selectedTargets,
}: {
  selectedTargets: MinimalTarget[] | undefined;
}) {
  const targetAttributeTags = new Set(['vehicle', 'company', 'other']);

  const targetAttrCtx = useContext(ctxTargetAttributes);
  const userCtx = useContext(ctxUser);

  const [rows, setRows] = useState<TargetAttributeDto[]>([]);
  const [changedRows, setChangedRows] = useState<TargetAttributeDto[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeDto[] | undefined>();
  const [fields, setFields] = useState<TypedField<IData>[]>([]);
  const [fieldsRevision, setFieldsRevision] = useState(0);
  const [searchText, setSearchText] = useState<string>('');
  const showNotification = useNotificationStore((state) => state.showNotification);
  const [resetKey, setResetKey] = useState(0);
  const [savedKeys, setSavedKeys] = useState<Set<string>>(new Set());

  const { t, i18n } = useTranslation();

  const filteredRows = useMemo(() => {
    if (!searchText.trim()) return rows;
    return rows.filter((row) => {
      const title = row.translatedKey?.toLowerCase() ?? '';
      const value = String(getContent(row, t) ?? '').toLowerCase();
      return title.includes(searchText.toLowerCase()) || value.includes(searchText.toLowerCase());
    });
  }, [searchText, rows]);

  useEffect(() => {
    let active = true;

    void buildFieldsFromRows(filteredRows).then((result) => {
      if (active) {
        setFields(result);
        setFieldsRevision((previous) => previous + 1);
      }
    });

    return () => {
      active = false;
    };
  }, [filteredRows]);

  useEffect(() => {
    async function loadPossibleValues() {
      const newValues = (await getVehicleTypes()).data;
      setVehicleTypes(newValues);
    }
    void loadPossibleValues();
  }, []);

  async function wait() {
    return new Promise((resolve) => {
      setTimeout(resolve, 200);
    });
  }

  async function loadTargetAttributes() {
    if (!selectedTargets || selectedTargets.length === 0) {
      setRows([]);
      return;
    }
    const selectedTargetIds = selectedTargets.map((target) => target.id);
    await wait();
    const perTargetAttributes = (await getCommonTargetAttributes(selectedTargetIds)).data;
    const newTargetAttributes = perTargetAttributes.attributes.map((row) => ({
      ...row,
      translatedKey: translateKey(row, t),
    }));
    setRows(newTargetAttributes);
  }

  useEffect(() => {
    void loadTargetAttributes();
  }, [selectedTargets]);

  useEffect(() => {
    if (changedRows.length > 0) {
      targetAttrCtx?.setUnsavedChanges(true);
    }
  }, [changedRows]);

  function handleRefresh() {
    setChangedRows([]);
    targetAttrCtx?.setUnsavedChanges(false);
    targetAttrCtx?.setDrawerOpen(false);
    setResetKey((prev) => prev + 1);
  }

  function mapType(type: string) {
    switch (type) {
      case '@date':
        return FieldType.Date;

      case '@boolean':
        return FieldType.Switch;

      case '@number':
        return FieldType.Text;

      case '@class':
        return FieldType.Tree;

      case '@list':
        return FieldType.Combo;

      default:
        return FieldType.Text;
    }
  }

  function groupRows(rows: TargetAttributeDto[]) {
    return rows.reduce<Record<string, TargetAttributeDto[]>>((map, row) => {
      const tags = row.tag?.length ? row.tag : ['Default'];

      tags.forEach((tag) => {
        map[tag] = [...(map[tag] ?? []), row];
      });

      return map;
    }, {});
  }

  function getGroupTitle(groupTitle: string, groupLength: number) {
    if (groupTitle === 'vehicle') {
      return `${t('targetAttributes.type.vehicle')} (${groupLength})`;
    }
    return `${groupTitle} (${groupLength})`;
  }

  async function getItemList(item: TargetAttributeDto) {
    if (item.type === '@class' && vehicleTypes) {
      return {
        itemList: vehicleTypes.map((v) => String(v.id)),
        tr: (id: string) => {
          // tr = translation => lényegében az id lesz a value, de szamok helyett a tipusa latszik
          const found = vehicleTypes.find((v) => String(v.id) === id);
          return found ? `${found.brand} ${found.type}` : id;
        },
      };
    }

    if (item.type === '@list' && item.possibleValueDescriber) {
      const newValues = (await getPossibleAttributeValues({ key: item.possibleValueDescriber })).data;
      return {
        itemList: newValues.map((v) => String(v.id)),
        tr: (id: string) => {
          const found = newValues.find((v) => String(v.id) === id);
          return found ? `${found.translation}` : id;
        },
      };
    }

    return {};
  }

  function isEmptyAttributeValue(value: unknown): boolean {
    if (Array.isArray(value)) {
      return value.length === 0 || value.every(isEmptyAttributeValue);
    }

    return value === undefined || value === null || value === '';
  }

  function isUnchangedVariousValue(row: TargetAttributeDto, currentValue: unknown) {
    if (!row.various || !isEmptyAttributeValue(row.value)) {
      return false;
    }

    if (row.type === '@boolean') {
      return currentValue === false || isEmptyAttributeValue(currentValue);
    }

    return isEmptyAttributeValue(currentValue);
  }

  function getDefaultValueForComparison(row: TargetAttributeDto) {
    if (row.various && isEmptyAttributeValue(row.value)) {
      return undefined;
    }

    if (row.type === '@date') {
      return getDateTimestamp(row.value);
    }

    return row.value?.id ?? getContent(row, t);
  }

  function getDateValue(value: unknown): Date | null {
    if (isEmptyAttributeValue(value) || value === 0 || value === '0') {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      const numericValue = Number(trimmedValue);
      const date =
        trimmedValue && Number.isFinite(numericValue) && /^\d+$/.test(trimmedValue)
          ? new Date(numericValue)
          : new Date(trimmedValue);

      return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
  }

  function getDateTimestamp(value: unknown) {
    return getDateValue(value)?.getTime();
  }

  function getDateIsoString(value: unknown) {
    return getDateValue(value)?.toISOString() ?? '';
  }

  function handleChange(newData: IData, initial: boolean) {
    if (initial) return;

    const newChangedRows: TargetAttributeDto[] = [];

    rows.forEach((row) => {
      const currentValue = newData[row.key];
      const defaultValue = getDefaultValueForComparison(row);

      if (isUnchangedVariousValue(row, currentValue)) {
        return;
      }

      if (isEmptyAttributeValue(defaultValue) && isEmptyAttributeValue(currentValue)) {
        return;
      }

      if (row.type === '@date') {
        const currentTimestamp = getDateTimestamp(currentValue);
        const defaultTimestamp = defaultValue as number | undefined;

        if (currentTimestamp !== defaultTimestamp) {
          newChangedRows.push({ ...row, value: currentTimestamp as unknown as TargetAttributeDtoValue });
        }

        return;
      }

      if (numericAttributeTypes.has(row.type)) {
        if (isEmptyAttributeValue(currentValue)) {
          newChangedRows.push({ ...row, value: undefined as unknown as TargetAttributeDtoValue });
          return;
        }

        const currentNumericValue = Number(currentValue);
        const defaultNumericValue = isEmptyAttributeValue(defaultValue) ? undefined : Number(defaultValue);

        if (!Number.isFinite(currentNumericValue)) {
          return;
        }

        if (defaultNumericValue !== currentNumericValue) {
          newChangedRows.push({ ...row, value: currentNumericValue as unknown as TargetAttributeDtoValue });
        }
      } else if (String(defaultValue) !== String(currentValue)) {
        newChangedRows.push({ ...row, value: currentValue as TargetAttributeDtoValue });
      }
    });

    setChangedRows(newChangedRows);
  }

  async function modifyAttributes() {
    if (!selectedTargets?.length) return;

    const selectedTargetIds = selectedTargets.map((target) => target.id);

    const changedValues = changedRows.map(({ key, type, value }) => {
      switch (type) {
        case TargetAttributeDtoType['@list']:
        case TargetAttributeDtoType['@class']: {
          const unwrapped = Array.isArray(value) ? value[0] : value; // ha tr-rel raktad be az id-kat, akkor Array-t kapsz, de mindig csak egy elemu lesz
          const id = typeof unwrapped === 'string' ? Number(unwrapped) : unwrapped; // es mindig string, kuldes elott at kell konvertalni, mert beszol a backend

          return { key, value: id as MinimalTargetAttributeDtoValue | undefined };
        }
        default:
          return { key, value };
      }
    });

    await updateTargetAttributes({ targetIds: selectedTargetIds, values: changedValues });
    setSavedKeys(new Set(changedRows.map((r) => r.key)));
    handleRefresh();
    setRows([]);
    targetAttrCtx?.setUnsavedChanges(false);
    targetAttrCtx?.setDrawerOpen(false);
    void loadTargetAttributes();
    showNotification('success', t('targetAttributes.successfulModification'), undefined, 3);
  }

  function getInputType(item: TargetAttributeDto) {
    if (numericAttributeTypes.has(item.type)) {
      return { inputType: 'number' };
    }
    return {};
  }

  function getDefaultAndPlaceholder(item: TargetAttributeDto) {
    const content = getContent(item, t) as unknown;

    if (item.various) {
      return { placeholder: content };
    }

    if (item.type === '@date') {
      return { defaultValue: getDateIsoString(item.value) };
    }

    return { defaultValue: item.value?.id ?? content };
  }

  function getFieldSx(hasVariousValues?: boolean) {
    return {
      ...(hasVariousValues
        ? {
            '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: 'warning.main',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'warning.main',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'warning.main',
            },
          }
        : {
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '& .MuiPickersTextField-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }),
      '& .MuiTypography-root': {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        maxWidth: '100%',
      },
    };
  }

  const getGroupStyle = (group: string) => {
    const type = targetAttributeTags.has(group) ? group : 'itrack-param';
    return groupStyles[type];
  };

  const groupStyles: Record<string, { background: string; iconBackground: string }> = {
    vehicle: {
      background: 'var(--mui-palette-primary-main)',
      iconBackground: 'var(--mui-palette-primary-dark)',
    },
    company: {
      background: 'var(--mui-palette-primary-light)',
      iconBackground: 'var(--mui-palette-primary-main)',
    },
    other: {
      background: 'var(--mui-palette-info-main)',
      iconBackground: 'var(--mui-palette-info-dark)',
    },
    'itrack-param': {
      background: 'var(--mui-palette-secondary-main)',
      iconBackground: 'color-mix(in srgb, var(--mui-palette-secondary-main) 70%, var(--mui-palette-text-primary) 30%)',
    },
  };

  async function buildFieldsFromRows(rows: TargetAttributeDto[]): Promise<TypedField<IData>[]> {
    const grouped = groupRows(rows);

    return Promise.all(
      Object.entries(grouped).map(async ([group, items]) => ({
        type: FieldType.Expansion,
        sx: {
          mb: 2,
          '& .MuiAccordionSummary-root': {
            backgroundColor: getGroupStyle(group).background,
            color: 'white',
            borderRadius: '25px',
            height: '50px',
            minHeight: '32px',
            width: '100%',
            fontSize: '13px',
            fontWeight: 600,
            marginTop: '5px',
          },
          '& .MuiAccordion-root': {
            boxShadow: 'none',
          },
          '& .MuiAccordionSummary-root svg': {
            color: 'white',
            backgroundColor: getGroupStyle(group).iconBackground,
            borderRadius: '25px',
          },
          '& .MuiAccordionDetails-root': {
            padding: '8px 0px',
          },
          '& .MuiAccordionSummary-content': {
            '& .MuiTypography-root': {
              flex: 3,
              wordBreak: 'break-word',
              whiteSpace: 'normal',
            },
          },
          '& .MuiAccordionSummary-content p:last-of-type': {
            display: 'none',
          },
          '& .MuiButtonBase-root.MuiAccordionSummary-root.Mui-expanded': {
            minHeight: '32px',
          },
          '& .MuiBox-root': {
            margin: '5px 0px',
          },
          '&.MuiGrid-item': {
            margin: 0,
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'rotate(180deg)',
          },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(0deg)',
          },
        },
        title: getGroupTitle(group, items.length),
        expansionOpened: true,
        fields: await Promise.all(
          items.map(async (item) => {
            const translationKey = `targetAttributes.keys.${item.key}`;
            const title = i18n.exists(translationKey) ? t(translationKey) : (item.translatedKey ?? item.key);
            return {
              type: FieldType.Box,
              fields: [
                {
                  type: mapType(item.type),
                  title,
                  name: item.key,
                  ...getDefaultAndPlaceholder(item),
                  ...(await getItemList(item)),
                  ...getInputType(item),
                  outlined: true,
                  sx: getFieldSx(item.various),
                },
              ] as TypedField<IData>[],
            };
          })
        ),
      }))
    );
  }

  const MyDateSlot = ({ value, onChange, title, disabled }: IDateSlot) => (
    <DateTimePickerSelect
      label={title ?? ''}
      currentValue={getDateValue(value)}
      disabled={disabled}
      minDate={null}
      maxDate={null}
      setSelectedDate={(newDate) => onChange(newDate.toISOString())}
      onClear={() => onChange('')}
    />
  );

  function MyTextSlot({
    value,
    onChange,
    title,
    name,
    inputType,
    outlined,
    placeholder,
    disabled,
    readonly,
    description,
    inputRows,
  }: ITextSlot) {
    const attributeType = rows.find((row) => row.key === name)?.type;

    function handleTextChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      const nextValue = event.target.value;

      if (inputType === 'text' && nextValue.startsWith(' ') && value === '') return;

      if (isEmptyAttributeValue(nextValue)) {
        onChange(nextValue);
        return;
      }

      if (attributeType && integerAttributeTypes.has(attributeType)) {
        const parsed = parseInt(nextValue, 10);
        if (parsed < 0) return;
        onChange(Number.isNaN(parsed) ? nextValue : parsed);
        return;
      }

      if (attributeType === TargetAttributeDtoType['@double']) {
        const parsed = parseFloat(nextValue);
        if (parsed < 0) return;
        onChange(Number.isNaN(parsed) ? nextValue : parsed);
        return;
      }

      onChange(nextValue);
    }

    return (
      <TextField
        data-testid={name}
        disabled={disabled}
        fullWidth
        helperText={description}
        label={title}
        multiline={Boolean(inputRows && inputRows > 1)}
        onChange={handleTextChange}
        placeholder={placeholder}
        rows={inputRows}
        type={inputType}
        value={value ?? ''}
        variant={outlined ? 'outlined' : 'standard'}
        slotProps={{
          input: {
            readOnly: readonly,
          },
        }}
      />
    );
  }

  function MyComboSlot({ value, onChange, title, itemList, outlined, placeholder, tr }: IComboSlot) {
    const translate = (item: string) => (tr ? tr(item, -1, {}) : item);

    return (
      <FormControl fullWidth variant={outlined ? 'outlined' : 'standard'}>
        <InputLabel>{title}</InputLabel>
        <Select
          value={value ?? ''}
          label={title}
          onChange={(e) => onChange(e.target.value as string)}
          MenuProps={{
            PaperProps: {
              sx: {
                bottom: 'var(--bottom-nav-bar-height)',
              },
            },
          }}
        >
          {placeholder && (
            <MenuItem value="">
              <em>{placeholder}</em>
            </MenuItem>
          )}
          {((itemList as string[]) ?? []).map((option) => (
            <MenuItem key={option} value={option}>
              {translate(option)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  function MyTreeSlot({ value, onChange, title }: ITreeSlot) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const vehicle = vehicleTypes?.find((v) => String(v.id) === String(value));
    const displayLabel = vehicle ? vehicle?.brand + ' ' + vehicle?.type : t('targetAttributes.variousValues');

    const Header = () => {
      return (
        <>
          <Stack direction={'row'} alignItems={'center'}>
            <IconButton
              data-qa-element-id="back_button"
              onClick={() => {
                setOpen(false);
              }}
              sx={{ color: 'text.primary' }}
            >
              <ArrowBack />
            </IconButton>
            {t('targetAttributes.vehicleCategoryMainText')}
          </Stack>
          <Typography variant="helperText" alignContent={'center'} ml={'8px'}>
            {t('targetAttributes.vehicleCategorySubText')}
          </Typography>
        </>
      );
    };

    return (
      <>
        <TextField
          fullWidth
          label={title}
          value={displayLabel}
          placeholder={t('targetAttributes.searchPlaceHolder')}
          variant="outlined"
          onClick={() => setOpen(true)}
          slotProps={{ input: { readOnly: true } }}
        />
        {open && (
          <Popup
            header={<Header />}
            body={
              <MobileVehicleTypeSelector
                selectedId={value ? String(value) : undefined}
                onSelect={(id) => {
                  onChange(id);
                  setOpen(false);
                }}
              />
            }
            footer={undefined}
            closePopup={async () => {}}
            leftText
            popupTitle={undefined}
            isFullScr
            showCloseButton={false}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          position: 'sticky',
          top: -1,
          alignItems: 'center',
          backgroundColor: 'white',
          gap: 1,
          zIndex: 1000,
          boxShadow: '0 4px 6px rgb(0 0 0 / 20%)',
          pb: '1px',
        }}
      >
        <SearchComponent
          searchText={searchText}
          setSearchText={setSearchText}
          showHistory={false}
          placeholder={t('targetAttributes.searchPlaceHolder')}
        />
        {!isRunningInApp() && (
          <FileDownload
            onClick={() => targetAttributesExport({ selectedTargets, userCtx, t, i18n })}
            className="export_button"
            sx={{ padding: '1px 6px' }}
            fontSize="large"
            color="primary"
          />
        )}
      </Box>

      {fields.length ? (
        <>
          {/*  Ez a OneSlostFactory kell ahhoz, hogy a base komponenseket le tudd cserelni altalad preferaltakra   */}
          <OneSlotFactory Date={MyDateSlot} Combo={MyComboSlot} Text={MyTextSlot} Tree={MyTreeSlot}>
            <Box
              sx={{
                ...changedRows.reduce(
                  (acc, row) => ({
                    ...acc,
                    [`& [data-testid="${row.key}"]`]: {
                      background: '#eb9d001f',
                    },
                  }),
                  {}
                ),
                ...Array.from(savedKeys).reduce(
                  (acc, key) => ({
                    ...acc,
                    [`& [data-testid="${key}"]`]: {
                      background: 'rgba(46, 125, 50, 0.12)',
                    },
                  }),
                  {}
                ),
                borderRadius: '10px',
              }}
            >
              <One key={`${fieldsRevision}-${resetKey}`} fields={fields} onChange={handleChange} />
            </Box>
          </OneSlotFactory>
          {changedRows.length > 0 && (
            <TargetAttributesSaveButton modifyAttributes={modifyAttributes} refresh={handleRefresh} />
          )}
        </>
      ) : (
        <DisplayNoResult searchText={searchText} />
      )}
      {targetAttrCtx?.drawerOpen && <UnsavedDrawer modifyAttributes={modifyAttributes} refresh={handleRefresh} />}
    </>
  );
}
